import { useState } from "react";
import ScrollReveal from "@/components/base/ScrollReveal";

const steps = [
  {
    number: "01",
    title: "初談健診",
    desc: "了解你的家庭結構、財務現況、既有保單與人生目標，釐清核心風險與優先缺口。",
    duration: "30–45 分鐘",
    icon: "ri-chat-smile-3-line",
  },
  {
    number: "02",
    title: "缺口分析",
    desc: "彙整保單、資產與現金流，製作專屬保障地圖與缺口清單，讓你清楚知道風險在哪裡。",
    duration: "3–5 個工作日",
    icon: "ri-file-search-line",
  },
  {
    number: "03",
    title: "方案設計",
    desc: "依據分析結果，提出整合性的調整建議與配置策略，讓每一分錢都花在刀口上。",
    duration: "一對一報告解說",
    icon: "ri-lightbulb-line",
  },
  {
    number: "04",
    title: "執行落地",
    desc: "協助保單調整、資產配置、信託設立與制度文件化，確保規劃真正轉變為行動。",
    duration: "持續進行",
    icon: "ri-shield-check-line",
  },
  {
    number: "05",
    title: "定期回顧",
    desc: "每半年至一年回顧規劃，隨著人生階段與市場變化，及時調整策略與配置。",
    duration: "長期陪伴",
    icon: "ri-refresh-line",
  },
];

export default function ProcessSection() {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <section className="py-16 md:py-24 bg-brand-card border-y border-brand-border">
      <div className="container-main">
        <ScrollReveal className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 border border-brand-border rounded-full px-5 py-2 mb-6">
            <span className="w-2 h-2 rounded-full bg-brand-gold" />
            <span className="text-brand-gold text-[0.9375rem] font-medium tracking-wide">
              合作流程
            </span>
          </div>
          <h2 className="text-white font-display font-bold text-[1.75rem] md:text-[2.5rem] leading-[1.3] mb-4">
            五步驟，從諮詢到長期守護
          </h2>
          <p className="text-brand-text-secondary text-[1.125rem] md:text-[1.25rem] leading-[1.7] max-w-[680px] mx-auto">
            第一次對話，通常只需要 30–45 分鐘。我們會陪你從釐清現況，到方案落地，再到長期回顧。
          </p>
        </ScrollReveal>

        {/* Desktop Timeline */}
        <div className="hidden md:block">
          {/* Connector line */}
          <div className="relative max-w-[960px] mx-auto mb-8">
            <div className="absolute top-[28px] left-[10%] right-[10%] h-[2px] bg-brand-border" />
            <div
              className="absolute top-[28px] left-[10%] h-[2px] bg-brand-gold transition-all duration-500"
              style={{
                width: `${(activeStep / (steps.length - 1)) * 80}%`,
              }}
            />
          </div>

          <div className="grid grid-cols-5 gap-4 max-w-[960px] mx-auto mb-12">
            {steps.map((step, idx) => (
              <button
                key={step.number}
                onClick={() => setActiveStep(idx)}
                className="flex flex-col items-center gap-3 cursor-pointer group"
              >
                <div
                  className={`w-14 h-14 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                    idx === activeStep
                      ? "bg-brand-gold border-brand-gold text-brand-black"
                      : idx < activeStep
                      ? "bg-brand-gold/20 border-brand-gold text-brand-gold"
                      : "bg-brand-black border-brand-border text-brand-text-secondary group-hover:border-brand-gold/50"
                  }`}
                >
                  <span className="font-bold text-[1.125rem]">
                    {step.number}
                  </span>
                </div>
                <span
                  className={`text-[0.9375rem] font-medium transition-colors ${
                    idx === activeStep
                      ? "text-brand-gold"
                      : "text-brand-text-secondary"
                  }`}
                >
                  {step.title}
                </span>
              </button>
            ))}
          </div>

          {/* Active Step Detail */}
          <ScrollReveal key={activeStep} className="max-w-[720px] mx-auto">
            <div className="bg-brand-black border border-brand-border rounded-2xl p-8 md:p-10 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-brand-gold/10 mx-auto mb-5">
                <span className="w-8 h-8 flex items-center justify-center text-brand-gold text-2xl">
                  <i className={steps[activeStep].icon} />
                </span>
              </div>
              <h3 className="text-white font-semibold text-[1.375rem] md:text-[1.75rem] mb-3">
                {steps[activeStep].title}
              </h3>
              <p className="text-brand-text-secondary text-[1.125rem] md:text-[1.25rem] leading-[1.7] mb-6 max-w-[560px] mx-auto">
                {steps[activeStep].desc}
              </p>
              <span className="inline-block border border-brand-gold/40 rounded-full px-4 py-1.5 text-brand-gold text-[0.9375rem] font-medium">
                <i className="ri-time-line mr-1.5" />
                {steps[activeStep].duration}
              </span>
            </div>
          </ScrollReveal>
        </div>

        {/* Mobile Vertical Steps */}
        <div className="md:hidden space-y-4">
          {steps.map((step, idx) => (
            <ScrollReveal key={step.number} delay={idx * 80}>
              <div
                className={`bg-brand-black border rounded-xl p-5 transition-all duration-300 ${
                  idx === activeStep
                    ? "border-brand-gold/60"
                    : "border-brand-border"
                }`}
                onClick={() => setActiveStep(idx)}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      idx <= activeStep
                        ? "bg-brand-gold text-brand-black"
                        : "bg-brand-gold/15 text-brand-gold"
                    }`}
                  >
                    <span className="font-bold text-[0.9375rem]">
                      {step.number}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-white font-semibold text-[1.125rem]">
                        {step.title}
                      </h3>
                      <span className="text-brand-gold text-[0.8125rem] font-medium">
                        {step.duration}
                      </span>
                    </div>
                    <p className="text-brand-text-secondary text-[1rem] leading-[1.65]">
                      {step.desc}
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}